import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiTrendingUp, FiCalendar, FiX, FiEye } from 'react-icons/fi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faInfoCircle, faList, faUser, faEye } from '@fortawesome/free-solid-svg-icons';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-material.css';
import '../styles/ag-grid-custom.css';
import apiService from '../services/apiService';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface ModalProps {
  title: string;
  data: any;
  onClose: () => void;
}

const DetailModal: React.FC<ModalProps> = ({ title, data, onClose }) => {
  // Special rendering for Rules (role requirements)
  const renderRules = (rulesData: any): JSX.Element => {
    if (!rulesData || typeof rulesData !== 'object') {
      return <div className="text-gray-500 italic p-4">No rules data available</div>;
    }

    // Check if there's a roleRequirements array
    const roleRequirements = rulesData.roleRequirements || rulesData.role_requirements || rulesData.roles;

    if (!roleRequirements || !Array.isArray(roleRequirements)) {
      // Fall back to generic table rendering
      return renderTableData(rulesData);
    }

    return (
      <div className="p-4">
        <h4 className="text-lg font-semibold text-gray-900 mb-3">Role Requirements</h4>
        <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Role</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Min - Max</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {roleRequirements.map((role: any, index: number) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">
                  {role.role || role.name || role.label || 'N/A'}
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                  {role.min !== undefined && role.max !== undefined
                    ? `${role.min} - ${role.max}`
                    : role.min !== undefined
                    ? `Min: ${role.min}`
                    : role.max !== undefined
                    ? `Max: ${role.max}`
                    : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Display other rule properties if they exist */}
        {Object.keys(rulesData).filter(key =>
          !['roleRequirements', 'role_requirements', 'roles'].includes(key)
        ).length > 0 && (
          <div className="mt-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3">Other Rules</h4>
            <table className="min-w-full divide-y divide-gray-200">
              <tbody className="divide-y divide-gray-200">
                {Object.entries(rulesData)
                  .filter(([key]) => !['roleRequirements', 'role_requirements', 'roles'].includes(key))
                  .map(([key, value]) => (
                    <tr key={key} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50 w-1/3">
                        {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()).trim()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {value === null || value === undefined ? (
                          <span className="text-gray-400 italic">null</span>
                        ) : typeof value === 'boolean' ? (
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                            value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {value ? 'Yes' : 'No'}
                          </span>
                        ) : typeof value === 'object' ? (
                          JSON.stringify(value, null, 2)
                        ) : (
                          String(value)
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Special rendering for Scoring System
  const renderScoringSystem = (scoringData: any): JSX.Element => {
    console.log('renderScoringSystem called with:', scoringData);
    console.log('scoringData type:', typeof scoringData);
    console.log('scoringData keys:', scoringData ? Object.keys(scoringData) : 'null');

    if (!scoringData || typeof scoringData !== 'object') {
      console.log('No scoring data or not an object');
      return <div className="text-gray-500 italic p-4">No scoring data available</div>;
    }

    // Check if scoringData has a nested structure (e.g., scoringData.rules or scoringData.data)
    const actualData = scoringData.rules || scoringData.data || scoringData.scoringRules || scoringData;

    // Group by sections (batting, bowling, fielding, etc.)
    const sections: { [key: string]: any[] } = {};

    Object.entries(actualData).forEach(([key, value]: [string, any]) => {
      console.log(`Checking key: ${key}, isArray: ${Array.isArray(value)}`, value);
      if (Array.isArray(value)) {
        sections[key] = value;
      }
    });

    console.log('Sections found:', sections);
    console.log('Number of sections:', Object.keys(sections).length);

    if (Object.keys(sections).length === 0) {
      console.log('No sections found in scoring data, falling back to table view');
      // Fall back to generic table rendering if no array sections found
      return renderTableData(actualData);
    }

    return (
      <div className="divide-y divide-gray-200">
        {Object.entries(sections).map(([sectionName, items]) => (
          <div key={sectionName} className="p-4">
            <h4 className="text-lg font-semibold text-gray-900 mb-3 capitalize">
              {sectionName.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
            </h4>
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Label</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Default Points</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item: any, index: number) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {item.label || item.name || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                      {item.default !== undefined ? item.default : item.points !== undefined ? item.points : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  // Convert data to array of key-value pairs for tabular display
  const renderTableData = (obj: any): JSX.Element => {
    if (obj === null || obj === undefined) {
      return <div className="text-gray-500 italic">No data available</div>;
    }

    // If it's an array
    if (Array.isArray(obj)) {
      if (obj.length === 0) {
        return <div className="text-gray-500 italic">Empty array</div>;
      }
      return (
        <div className="space-y-2">
          {obj.map((item, index) => (
            <div key={index} className="border border-gray-200 rounded p-3 bg-gray-50">
              <div className="font-semibold text-gray-700 mb-2">Item {index + 1}</div>
              {typeof item === 'object' ? renderTableData(item) : <div>{String(item)}</div>}
            </div>
          ))}
        </div>
      );
    }

    // If it's an object
    if (typeof obj === 'object') {
      const entries = Object.entries(obj);
      if (entries.length === 0) {
        return <div className="text-gray-500 italic">Empty object</div>;
      }
      return (
        <table className="min-w-full divide-y divide-gray-200">
          <tbody className="divide-y divide-gray-200">
            {entries.map(([key, value]) => (
              <tr key={key} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50 w-1/3">
                  {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()).trim()}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {value === null || value === undefined ? (
                    <span className="text-gray-400 italic">null</span>
                  ) : typeof value === 'object' ? (
                    <div className="border-l-2 border-blue-200 pl-3">{renderTableData(value)}</div>
                  ) : typeof value === 'boolean' ? (
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                      value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {value ? 'Yes' : 'No'}
                    </span>
                  ) : (
                    String(value)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    // Primitive value
    return <div>{String(obj)}</div>;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-dark-400 rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden border border-gray-200 dark:border-dark-300/40" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-300/40 bg-gray-50 dark:bg-dark-500">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full p-1 hover:bg-gray-200 dark:hover:bg-dark-400/50"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto max-h-[calc(85vh-80px)]">
          {title === 'Scoring System'
            ? renderScoringSystem(data)
            : title === 'Rules'
            ? renderRules(data)
            : renderTableData(data)}
        </div>
      </div>
    </div>
  );
};

const Contests: React.FC = () => {
  const navigate = useNavigate();
  const [series, setSeries] = useState<any[]>([]);
  const [selectedSeries, setSelectedSeries] = useState<string>('');
  const [contests, setContests] = useState<any[]>([]);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [loadingContests, setLoadingContests] = useState(false);
  const [error, setError] = useState('');
  const [modalData, setModalData] = useState<{ type: string; data: any } | null>(null);

  useEffect(() => {
    fetchSeries();
  }, []);

  useEffect(() => {
    if (selectedSeries) {
      fetchContests(selectedSeries);
    } else {
      setContests([]);
    }
  }, [selectedSeries]);

  const fetchSeries = async () => {
    try {
      setLoadingSeries(true);
      const response: any = await apiService.getSeries();
      const apiSeries = response?.data || response || [];

      console.log('All series from API:', apiSeries);
      console.log('First series fields:', apiSeries[0]);

      // Filter only published series using the 'publish' key
      const publishedSeries = apiSeries.filter((s: any) => {
        console.log(`Series "${s.name || s.title}":`, {
          publish: s.publish,
          matchesFilter: s.publish === true
        });

        return s.publish === true;
      });

      console.log('Filtered published series:', publishedSeries);

      setSeries(publishedSeries.map((s: any) => ({ id: s.seriesId || s.id, name: s.name || s.title })));
      setError('');
    } catch (err) {
      setError('Failed to load series');
      console.error(err);
    } finally {
      setLoadingSeries(false);
    }
  };

  const fetchContests = async (seriesId: string) => {
    try {
      setLoadingContests(true);
      const data = await apiService.getContests(seriesId, true);
      console.log('Raw contests data:', data);
      console.log('First contest:', data?.[0]);
      setContests(data || []);
      setError('');
    } catch (err) {
      setError('Failed to load contests');
      console.error(err);
    } finally {
      setLoadingContests(false);
    }
  };

  const getStatusColor = (status: string) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower.includes('active') || statusLower.includes('live')) return 'bg-green-100 text-green-800 border-green-300';
    if (statusLower.includes('upcoming') || statusLower.includes('scheduled')) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (statusLower.includes('completed') || statusLower.includes('finished')) return 'bg-gray-100 text-gray-800 border-gray-300';
    return 'bg-gray-100 text-gray-600 border-gray-200';
  };

  // Generate dynamic columns from first item in contests array
  const columnDefs: ColDef[] = useMemo(() => {
    if (contests.length === 0) return [];

    const firstItem = contests[0];
    const keys = Object.keys(firstItem);

    console.log('Available fields in contest data:', keys);

    // Fields to exclude from grid (will be shown in modals)
    const excludedFields = ['rules', 'scoringSystem', 'scoring_system', 'owner', 'ownerInfo', 'owner_info', 'seriesName', 'series_name'];

    // Fields that are IDs (keep only contestId, exclude others including generic 'id')
    const idFieldsToExclude = ['seriesId', 'series_id', 'ownerId', 'owner_id', 'userId', 'user_id', 'id'];

    // Filter and map columns
    const dataColumns = keys
      .filter((key) => {
        const keyLower = key.toLowerCase();
        const value = firstItem[key];
        // Exclude specific fields meant for modals
        if (excludedFields.some(excluded => keyLower === excluded.toLowerCase())) return false;
        // Exclude unnecessary ID fields
        if (idFieldsToExclude.some(excluded => keyLower === excluded.toLowerCase())) return false;
        // Exclude object/array fields that aren't handled specially (they would show as [object Object])
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) return false;
        // Exclude array fields
        if (Array.isArray(value)) return false;
        return true;
      })
      .map((key) => {
        const value = firstItem[key];
        const isDate = key.toLowerCase().includes('date') || key.toLowerCase().includes('time') || key.toLowerCase().includes('createdat') || key.toLowerCase().includes('updatedat');
        const isBoolean = typeof value === 'boolean';
        const isNumber = typeof value === 'number' && !key.toLowerCase().includes('id');

        // Format header name: convert camelCase/snake_case to Title Case
        const headerName = key
          .replace(/([A-Z])/g, ' $1')
          .replace(/_/g, ' ')
          .replace(/^\w/, (c) => c.toUpperCase())
          .trim();

        const columnDef: ColDef = {
          field: key,
          headerName: headerName,
          filter: isNumber ? 'agNumberColumnFilter' : isDate ? 'agDateColumnFilter' : 'agTextColumnFilter',
          sortable: true,
          resizable: true,
          minWidth: 150,
          flex: 1,
        };

        // Add custom rendering for specific field types
        if (isBoolean) {
          columnDef.minWidth = 100;
          columnDef.flex = 0.5;
          columnDef.cellRenderer = (params: ICellRendererParams) => (
            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
              params.value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {params.value ? 'Yes' : 'No'}
            </span>
          );
        } else if (key.toLowerCase().includes('status')) {
          columnDef.minWidth = 140;
          columnDef.flex = 0.8;
          columnDef.cellRenderer = (params: ICellRendererParams) => {
            const statusColor = getStatusColor(params.value || '');
            return (
              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold border ${statusColor}`}>
                {params.value || 'N/A'}
              </span>
            );
          };
        } else if (isDate) {
          columnDef.minWidth = 200;
          columnDef.flex = 1.2;
          columnDef.valueFormatter = (params) => {
            if (!params.value) return 'N/A';
            try {
              return new Date(params.value).toLocaleString();
            } catch {
              return params.value;
            }
          };
        } else if (key.toLowerCase().includes('id')) {
          columnDef.minWidth = 250;
          columnDef.flex = 1.5;
          columnDef.cellRenderer = (params: ICellRendererParams) => (
            <div className="font-mono text-xs">{params.value || 'N/A'}</div>
          );
        }

        return columnDef;
      });

    // Add Actions column with buttons for View Details, Rules, Scoring, Owner Info
    const actionsColumn: ColDef = {
      field: 'actions',
      headerName: 'Actions',
      width: 400,
      pinned: 'right',
      sortable: false,
      filter: false,
      resizable: false,
      suppressSizeToFit: true,
      cellRenderer: (params: ICellRendererParams) => {
        const contest = params.data;
        const contestId = contest._id || contest.id;
        const hasRules = contest.rules || contest.Rules;
        const hasScoring = contest.scoringSystem || contest.scoring_system || contest.ScoringSystem;
        const hasOwner = contest.owner || contest.ownerInfo || contest.owner_info || contest.Owner;

        return (
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/dashboard/contests/${contestId}`, { state: { contest } })}
              className="inline-flex items-center px-3 py-1 bg-indigo-50 text-indigo-700 rounded-md hover:bg-indigo-100 transition-colors text-xs font-medium"
            >
              <FontAwesomeIcon icon={faEye} className="mr-1" />
              Details
            </button>
            {hasRules && (
              <button
                onClick={() => setModalData({ type: 'Rules', data: hasRules })}
                className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors text-xs font-medium"
              >
                <FontAwesomeIcon icon={faList} className="mr-1" />
                Rules
              </button>
            )}
            {hasScoring && (
              <button
                onClick={() => {
                  console.log('Opening scoring modal with data:', hasScoring);
                  setModalData({ type: 'Scoring System', data: hasScoring });
                }}
                className="inline-flex items-center px-3 py-1 bg-green-50 text-green-700 rounded-md hover:bg-green-100 transition-colors text-xs font-medium"
              >
                <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                Scoring
              </button>
            )}
            {hasOwner && (
              <button
                onClick={() => setModalData({ type: 'Owner Info', data: hasOwner })}
                className="inline-flex items-center px-3 py-1 bg-purple-50 text-purple-700 rounded-md hover:bg-purple-100 transition-colors text-xs font-medium"
              >
                <FontAwesomeIcon icon={faUser} className="mr-1" />
                Owner
              </button>
            )}
          </div>
        );
      },
    };

    return [...dataColumns, actionsColumn];
  }, [contests]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: true,
    minWidth: 120,
    wrapText: true,
    autoHeight: false,
    // Safety net: format any remaining object values as JSON or display appropriately
    valueFormatter: (params: any) => {
      if (params.value === null || params.value === undefined) return '';
      if (typeof params.value === 'object') {
        // For arrays, show count
        if (Array.isArray(params.value)) {
          return `[${params.value.length} items]`;
        }
        // For objects, try to show a meaningful representation
        const keys = Object.keys(params.value);
        if (keys.length === 0) return '{}';
        // Try common display fields
        if (params.value.name) return params.value.name;
        if (params.value.title) return params.value.title;
        if (params.value.label) return params.value.label;
        return `{${keys.length} fields}`;
      }
      return params.value;
    },
  }), []);

  const onGridReady = (params: any) => {
    // Auto-size columns first
    params.api.autoSizeAllColumns(false);

    // Then add padding to prevent overlap
    setTimeout(() => {
      const allColumns = params.api.getColumns();
      if (allColumns) {
        const widthConfig: any = {};
        allColumns.forEach((column: any) => {
          const currentWidth = column.getActualWidth();
          const colId = column.getColId();
          widthConfig[colId] = currentWidth + 20;
        });
        params.api.setColumnWidths(Object.entries(widthConfig).map(([key, width]) => ({
          key,
          newWidth: width as number
        })));
      }
    }, 100);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Contests</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {selectedSeries ? `Showing ${contests.length} contests` : 'Select a series to view its contests'}
          </p>
        </div>
        {selectedSeries && (
          <button
            onClick={() => fetchContests(selectedSeries)}
            disabled={loadingContests}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-dark-300/40 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-400/60 hover:bg-gray-50 dark:hover:bg-dark-400/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-primary-500 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faRefresh} className={`mr-2 ${loadingContests ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        )}
      </div>

      {/* Series Selector */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg p-4">
        <label htmlFor="series-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          <FiCalendar className="inline h-4 w-4 mr-1" />
          Select Series
        </label>
        <select
          id="series-select"
          value={selectedSeries}
          onChange={(e) => setSelectedSeries(e.target.value)}
          className="block w-full max-w-md px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">-- Select a series --</option>
          {loadingSeries ? (
            <option disabled>Loading...</option>
          ) : (
            series.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.name}
              </option>
            ))
          )}
        </select>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-danger-500/20 border border-red-200 dark:border-danger-500/30 rounded-lg p-4">
          <div className="text-sm text-red-800 dark:text-danger-500">{error}</div>
        </div>
      )}

      {/* AG Grid */}
      {selectedSeries && (
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg overflow-hidden">
          <div className="ag-theme-material" style={{ height: 'calc(100vh - 400px)', minHeight: '400px', width: '100%' }}>
            <AgGridReact
              rowData={contests}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={25}
              paginationPageSizeSelector={[10, 25, 50, 100]}
              loading={loadingContests}
              rowHeight={60}
              animateRows={true}
              enableCellTextSelection={true}
              onGridReady={onGridReady}
              onFirstDataRendered={(params) => params.api.autoSizeAllColumns()}
            />
          </div>
        </div>
      )}

      {/* Empty State - Only show when series is selected but no contests */}
      {selectedSeries && !loadingContests && contests.length === 0 && (
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="text-center py-12">
            <div className="mx-auto h-16 w-16 bg-gray-100 dark:bg-dark-300/40 rounded-full flex items-center justify-center mb-4">
              <FiTrendingUp className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No contests found</h3>
            <p className="text-gray-600 dark:text-gray-400">No contests found for the selected series</p>
          </div>
        </div>
      )}

      {/* No Series Selected State */}
      {!selectedSeries && (
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="text-center py-12">
            <div className="mx-auto h-16 w-16 bg-blue-100 dark:bg-primary-500/20 rounded-full flex items-center justify-center mb-4 border border-blue-200 dark:border-primary-500/30">
              <FiCalendar className="h-8 w-8 text-blue-500 dark:text-primary-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Select a series</h3>
            <p className="text-gray-600 dark:text-gray-400">Please select a series from the dropdown above to view its contests</p>
          </div>
        </div>
      )}

      {/* Detail Modals */}
      {modalData && (
        <DetailModal
          title={modalData.type}
          data={modalData.data}
          onClose={() => setModalData(null)}
        />
      )}
    </div>
  );
};

export default Contests;
